import { useState } from 'react';
import PropTypes from 'prop-types';
import { LocationsMap } from './LocationsMap';
import TextLink from '../../components/ui/TextLink';
import { useTranslation } from '../../i18n/I18nContext';
import { groupOpeningHours } from '../../utils/groupOpeningHours';
import './PlacesBlock.css';

/**
 * Салоны: колонка с адресами слева, общая карта справа (#1335, вариант «A»).
 *
 * Один блок на главную и на «Showroom»: раньше там были две разные раскладки
 * одного и того же — врезка в карту на главной, бенто-ячейка с карточками на
 * «Showroom». Каждый салон: название (H3 24/32), адрес, часы работы, ссылка
 * «на карте». Наведение на салон зажигает его метку, клик по метке выделяет
 * салон — выбор общий у списка и карты.
 *
 * Только витрина: файл в `pages/`, в бандл дилера не едет.
 *
 * @param {object} props
 * @param {Array<object>} props.locations — записи `useContactInfo().locations`.
 * @param {Record<string, string>} [props.hours] — недельное расписание.
 * @param {import('react').ReactNode} [props.title] — H2 блока; без него
 *   колонка начинается сразу с салонов (заголовок даёт страница).
 * @param {import('react').ReactNode} [props.lead] — подзаголовок под H2.
 * @param {boolean} [props.details] — часы и ссылка «на карте» у салонов.
 *   На главной их нет (вариант «A»: название и адрес), на «Showroom» есть.
 */
export const PlacesBlock = ({
  locations,
  hours,
  title,
  lead,
  details = true,
}) => {
  const { t, language } = useTranslation();
  const [selected, setSelected] = useState(null);
  const openingHours = groupOpeningHours(hours, language);

  return (
    <div className="places">
      <div className="places__list">
        {title && (
          <div className="places__head">
            <h2 className="places__title">{title}</h2>
            {lead && <p className="places__lead">{lead}</p>}
          </div>
        )}
        {locations.map((location) => (
          <article
            key={location.name}
            className={`places__item${
              selected === location.name ? ' places__item--selected' : ''
            }`}
            onMouseEnter={() => setSelected(location.name)}
            onFocus={() => setSelected(location.name)}
          >
            <h3 className="places__name">{location.name}</h3>
            <address className="places__address">
              {location.address}
              <br />
              {location.city}
              {location.country ? `, ${location.country}` : ''}
            </address>
            {details && openingHours.length > 0 && (
              <dl className="places__hours">
                {openingHours.map(({ days, time }) => (
                  <div className="places__hours-row" key={days}>
                    <dt>{days}</dt>
                    <dd>{time}</dd>
                  </div>
                ))}
              </dl>
            )}
            {details && location.mapsUrl && (
              <TextLink
                href={location.mapsUrl}
                external
                arrow
                className="places__link"
              >
                {t('showcase.footer.viewOnMap', 'View on map')}
              </TextLink>
            )}
          </article>
        ))}
      </div>
      {/* Кадр передаём явно: компонент ставит из него `aspect-ratio` и по
          нему вписывает метки. */}
      <div className="places__map">
        <LocationsMap
          locations={locations}
          selected={selected}
          onSelect={setSelected}
          width={880}
          height={440}
        />
      </div>
    </div>
  );
};

PlacesBlock.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      address: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      mapsUrl: PropTypes.string,
      coords: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
    })
  ).isRequired,
  hours: PropTypes.object,
  title: PropTypes.node,
  lead: PropTypes.node,
  details: PropTypes.bool,
};

export default PlacesBlock;
